/**
 * Member Detail Page
 *
 * Drill-down view for a single member.
 * Shows activity, groups, and attendance history.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { founderDashboardAPI } from '../../services/api';
import { useEnvironment } from '../../context/EnvironmentContext';
import KPICard from '../../components/founder-dashboard/KPICard';
import DataTable from '../../components/founder-dashboard/DataTable';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import {
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

export default function MemberDetailPage() {
  const { currentEnv } = useEnvironment();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null); // Track which env the data/error belongs to

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getMember(id);
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[MemberDetailPage] Error:', err);
      const errorData = err.response?.data?.error;
      const errorMsg = typeof errorData === 'string' ? errorData :
                       errorData?.message || err.message || 'Failed to load data';
      setError(errorMsg);
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear all state and refetch when environment changes
    setData(null);
    setError(null);
    fetchData();
  }, [id, currentEnv]);

  const groupColumns = [
    {
      key: 'name',
      header: 'Group Name',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{value}</span>
          {row.role === 'leader' && (
            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
              Leader
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'attendanceRate',
      header: 'Attendance',
      render: (value) => (value !== null ? `${(value * 100).toFixed(0)}%` : '-'),
    },
  ];

  const attendanceColumns = [
    {
      key: 'eventName',
      header: 'Event',
    },
    {
      key: 'date',
      header: 'Date',
      render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => (
        <span
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            value === 'present'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {value === 'present' ? (
            <CheckCircleIcon className="w-3.5 h-3.5" />
          ) : (
            <XCircleIcon className="w-3.5 h-3.5" />
          )}
          {value === 'present' ? 'Present' : 'Absent'}
        </span>
      ),
    },
  ];

  // Only show error if it's from the current environment
  if (error && dataEnv === currentEnv) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Error loading member</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const member = data?.member || {};
  const organizations = data?.organizations || [];
  const groups = data?.groups || [];
  const activity = data?.activity || {};
  const attendanceHistory = data?.attendanceHistory || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </button>
          {loading ? (
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
          ) : (
            <h2 className="text-2xl font-bold text-gray-900">{member.name}</h2>
          )}
        </div>
        <RefreshIndicator onRefresh={fetchData} loading={loading} />
      </div>

      {/* Member Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gray-100 rounded-lg">
              <EnvelopeIcon className="w-6 h-6 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="text-gray-900">{loading ? '-' : member.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gray-100 rounded-lg">
              <CalendarIcon className="w-6 h-6 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Member Since</p>
              <p className="text-gray-900">
                {loading ? '-' : new Date(member.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gray-100 rounded-lg">
              <UserIcon className="w-6 h-6 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Last Active</p>
              <p className="text-gray-900">
                {loading
                  ? '-'
                  : activity.lastActiveAt
                  ? new Date(activity.lastActiveAt).toLocaleDateString()
                  : 'Never'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Organizations */}
      {organizations.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Organizations ({organizations.length})
          </h3>
          <div className="space-y-3">
            {organizations.map((org) => (
              <div
                key={org.id}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
              >
                <div>
                  <p className="font-medium text-gray-900">{org.name}</p>
                  <p className="text-xs text-gray-500 capitalize">
                    {org.role?.replace(/_/g, ' ')} &bull; Joined{' '}
                    {new Date(org.joinedAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/founder-dashboard/organizations/${org.id}`)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  View
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard
          title="Prayers Submitted"
          value={activity.prayersSubmitted}
          loading={loading}
        />
        <KPICard
          title="Events Attended"
          value={activity.eventsAttended}
          loading={loading}
        />
        <KPICard
          title="Events Created"
          value={activity.eventsCreated}
          loading={loading}
        />
      </div>

      {/* Groups Table */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Groups ({groups.length})
        </h3>
        <DataTable
          data={groups}
          columns={groupColumns}
          loading={loading}
          onRowClick={(group) => navigate(`/founder-dashboard/groups/${group.id}`)}
          emptyMessage="Not a member of any groups"
        />
      </div>

      {/* Attendance History */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Recent Attendance
        </h3>
        <DataTable
          data={attendanceHistory}
          columns={attendanceColumns}
          loading={loading}
          emptyMessage="No attendance records"
        />
      </div>
    </div>
  );
}
